<template>
	<v-tooltip top :disabled="isInbound && isHavingCall">
		<template #activator="{ on }">
			<div v-on="on">
				<!-- Inbound (pickup) button -->
				<v-btn
					v-if="isInbound"
					fab
					elevation="0"
					color="primary"
					small
					@click="handleAccept"
				>
					<v-icon color="white"> mdi-phone </v-icon>
				</v-btn>

				<!-- Outbound (call contact) button -->
				<v-btn
					v-else
					fab
					elevation="0"
					color="primary"
					large
					:disabled="!enableOutgoingCalls || !isCallAvailable"
					:outlined="!isCallAvailable"
					@click="callContact"
				>
					<v-icon color="white"> {{ icon }} </v-icon>
				</v-btn>
			</div>
		</template>
		<span class="wrapped-text">{{ buttonText }}</span>
	</v-tooltip>
</template>
<script>
import { mapGetters, mapMutations } from "vuex";
import { mapFields } from "vuex-map-fields";
import { bus } from "@/main.js";
import { getAudioDeviceManager } from "@/composables/useAudioDeviceManager";
import { getMicrophoneStatus } from "@/common/utils";

export default {
	name: "DialButton",

	props: {
		isInbound: {
			type: Boolean,
			default: false
		}
	},

	computed: {
		...mapGetters("login", ["userGroup"]),
		...mapGetters("conversation", ["phoneNumber", "singleConversation"]),
		...mapGetters("conversationList", ["getPatientByConvoSid"]),
		...mapGetters("voice", ["activeCall", "isMicroOn", "callDetails", "isReady", "isOnCall", "hasIncomingCall"]),
		...mapGetters("clinic", ["enableOutgoingCalls", "webCallsEnabled"]),
		...mapFields("contact", ["toggleContactDrawer"]),

		buttonText() {
			if (!this.isMicroOn) {
				return this.$t("no-microphone");
			}

			if (this.isHavingCall && !this.isCallAvailable) {
				return this.$t("call-in-progress");
			}

			if (this.isInbound) {
				return this.$t("answer");
			}

			if (!this.enableOutgoingCalls) {
				return this.$t("call-disabled-by-permission");
			}

			return this.$t(this.isCallAvailable ? "call-contact" : "call-disabled");
		},

		icon() {
			return !this.isMicroOn ? "mdi-phone-alert" : this.isInbound ? "mdi-phone" : "mdi-phone-in-talk";
		},

		isCallAvailable() {
			return this.webCallsEnabled && this.isReady && !this.isOnCall && !this.hasIncomingCall && this.isMicroOn;
		},

		isHavingCall() {
			return this.isOnCall || this.hasIncomingCall;
		}
	},

	methods: {
		...mapMutations("voice", ["setIsMicroGuideOpen"]),

		async callContact() {
			document.activeElement.blur();
			try {
				const isMicrophonePermissionGranted = await getMicrophoneStatus();

				if (isMicrophonePermissionGranted) {
					await this.outboundCall();
				} else {
					await this.getLocalStream();
				}
			} catch (err) {
				console.error(err);
			}
		},

		async handleAccept() {
			// Clear any digits from the dialpad before answer
			try {
				const isMicrophonePermissionGranted = await getMicrophoneStatus();

				if (!isMicrophonePermissionGranted) {
					this.setIsMicroGuideOpen(true);
				} else {
					await this.activeCall.accept();

					if (this.activeCallConvoSid) {
						this.displayConversation(this.activeCallConvoSid);
					}
				}
			} catch (err) {
				console.error(err);
			}
		},

		async handleCall() {
			return this.isInbound ? this.handleAccept() : this.callContact();
		},

		async getLocalStream() {
			try {
				const audioDeviceManager = getAudioDeviceManager();
				const stream = await audioDeviceManager.getUserMedia();

				stream.getTracks().forEach(track => track.stop());
				await this.outboundCall();
			} catch (err) {
				this.setIsMicroGuideOpen(true);
			}
		},

		async outboundCall() {
			if (!this.phoneNumber) {
				throw new Error(this.$t("error.call-contact"));
			}
			const patient = this.getPatientByConvoSid(this.singleConversation?.sid);

			if (this.$vuetify.breakpoint.xs) this.toggleContactDrawer = false;
			bus.$emit("start-outbound-call", { phoneNumber: this.phoneNumber, patient, conversationSid: this.singleConversation?.sid });
		}
	}
};
</script>
<style scoped>

.wrapped-text {
	display: inline-block;
	white-space: normal;
	word-break: break-word;
	max-width: 200px;
}

</style>
